import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { X } from "lucide-react";

export default function Devotionals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/devotionals").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-bone pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Daily Bread</span>
          <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight mt-4">Devotionals</h1>
          <p className="font-sans text-charcoal/60 mt-4 max-w-xl">Scripture-rooted encouragement for physical, emotional, and spiritual wellness.</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          {items.map((d, i) => (
            <Reveal key={d.id} delay={(i % 3) * 0.08}>
              <button onClick={() => setActive(d)} data-testid={`devotional-${d.id}`}
                className="group block text-left border border-charcoal/10 hover:border-charcoal transition-colors duration-500 w-full">
                <div className="overflow-hidden h-56">
                  <img src={d.image} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{d.category} · {d.scripture}</span>
                  <h3 className="font-serif text-2xl mt-3 tracking-tight">{d.title}</h3>
                  <p className="font-sans text-sm text-charcoal/60 mt-3 line-clamp-3">{d.body}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {user?.role === "admin" && <AdminCreate onCreated={load} />}
      </div>

      {active && (
        <div className="fixed inset-0 z-[60] bg-charcoal/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setActive(null)}>
          <div className="bg-bone max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="devotional-modal">
            <div className="relative h-64">
              <img src={active.image} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setActive(null)} className="absolute top-4 right-4 bg-bone p-2" data-testid="close-modal"><X size={18} /></button>
            </div>
            <div className="p-10">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{active.category} · {active.scripture}</span>
              <h2 className="font-serif font-light text-4xl mt-4 tracking-tight">{active.title}</h2>
              <p className="font-sans text-charcoal/80 leading-relaxed mt-6 whitespace-pre-line">{active.body}</p>
              <span className="font-mono text-xs uppercase tracking-widest text-charcoal/40 mt-8 block">— {active.author}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminCreate({ onCreated }) {
  const [f, setF] = useState({ title: "", scripture: "", body: "", category: "Spiritual", image: "https://images.unsplash.com/photo-1615260478820-ddc3a5051ff2" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/devotionals", f); setF({ ...f, title: "", scripture: "", body: "" }); onCreated(); } finally { setBusy(false); }
  };
  return (
    <div className="mt-24 border-t border-charcoal/20 pt-12">
      <h2 className="font-serif text-3xl mb-8">New Devotional</h2>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-devotional-form">
        <input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="dev-title" className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-olive" />
        <input placeholder="Scripture (e.g. John 3:16)" value={f.scripture} onChange={(e) => setF({ ...f, scripture: e.target.value })} data-testid="dev-scripture" className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-olive" />
        <input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-olive" />
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-olive">
          <option>Spiritual</option><option>Physical</option><option>Emotional</option><option>Leadership</option>
        </select>
        <textarea placeholder="Body" rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} required data-testid="dev-body" className="md:col-span-2 border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-olive resize-none" />
        <button disabled={busy} data-testid="dev-submit" className="md:col-span-2 bg-charcoal text-bone py-4 font-mono text-xs uppercase tracking-widest hover:bg-olive transition-colors disabled:opacity-50">{busy ? "Publishing..." : "Publish Devotional"}</button>
      </form>
    </div>
  );
}
